import { useState, useCallback } from 'react'
import { useMessages } from '../../hooks/useMessages'
import { useAuth } from '../../hooks/useAuth'
import ConversationList from './components/ConversationList'
import ChatArea from './components/ChatArea'
import NewMessageModal from './components/NewMessageModal'
import CreateGroupModal from './components/CreateGroupModal'
import Header from '../../components/feature/Header'
import Footer from '../home/components/Footer'

const MessagesPage = () => {
  const { user } = useAuth()
  const {
    conversations,
    messages,
    currentConversation,
    setCurrentConversation,
    sendMessage,
    deleteMessage,
    deleteConversation,
    startConversation,
    createGroupConversation,
    leaveGroup,
    removeMember,
    updateGroupSettings,
    inviteMembers,
    markAsRead,
    fetchConversations,
    loading,
    messagesLoading
  } = useMessages()

  const [searchQuery, setSearchQuery] = useState('')
  const [showNewMessage, setShowNewMessage] = useState(false)
  const [showCreateGroup, setShowCreateGroup] = useState(false)
  const [showMobileChat, setShowMobileChat] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showLeaveGroupConfirm, setShowLeaveGroupConfirm] = useState(false)
  const [activeFilter, setActiveFilter] = useState<'all' | 'unread' | 'groups'>('all')

  const activeConversation = conversations.find(c => c.id === currentConversation) || null

  // Filter conversations based on active filter
  const filteredConversations = conversations.filter(conv => {
    if (activeFilter === 'unread') {
      return (conv.unread_count || 0) > 0
    }
    if (activeFilter === 'groups') {
      return conv.is_group
    }
    return true
  })

  const handleSelectConversation = useCallback((id: string) => {
    setCurrentConversation(id)
    markAsRead(id)
    setShowMobileChat(true)
  }, [setCurrentConversation, markAsRead])

  const handleSendMessage = useCallback(async (content: string) => {
    if (!currentConversation) return
    await sendMessage(currentConversation, content)
  }, [currentConversation, sendMessage])

  const handleStartConversation = useCallback(async (userId: string) => {
    const convId = await startConversation(userId)
    if (convId) {
      setCurrentConversation(convId)
      setShowMobileChat(true)
      await fetchConversations()
    }
  }, [startConversation, setCurrentConversation, fetchConversations])

  const handleCreateGroup = useCallback(async (name: string, memberIds: string[]) => {
    const convId = await createGroupConversation(name, memberIds)
    if (convId) {
      setCurrentConversation(convId)
      setShowMobileChat(true)
      await fetchConversations()
    }
  }, [createGroupConversation, setCurrentConversation, fetchConversations])

  const handleBack = useCallback(() => {
    setShowMobileChat(false)
  }, [])

  const handleDeleteConversation = useCallback(() => {
    setShowDeleteConfirm(true)
  }, [])

  const handleLeaveGroup = useCallback(() => {
    setShowLeaveGroupConfirm(true)
  }, [])

  const handleRemoveMember = useCallback(async (userId: string) => {
    if (currentConversation) {
      await removeMember(currentConversation, userId)
    }
  }, [currentConversation, removeMember])

  const handleUpdateGroupSettings = useCallback(async (name: string, icon: string) => {
    if (currentConversation) {
      await updateGroupSettings(currentConversation, name, icon)
    }
  }, [currentConversation, updateGroupSettings])

  const handleInviteMembers = useCallback(async (userIds: string[]) => {
    if (currentConversation) {
      await inviteMembers(currentConversation, userIds)
    }
  }, [currentConversation, inviteMembers])

  const handleDeleteMessage = useCallback(async (messageId: string) => {
    if (currentConversation) {
      await deleteMessage(messageId, currentConversation)
    }
  }, [currentConversation, deleteMessage])

  const confirmDelete = useCallback(async () => {
    if (currentConversation) {
      await deleteConversation(currentConversation)
    }
    setCurrentConversation(null)
    setShowMobileChat(false)
    setShowDeleteConfirm(false)
  }, [currentConversation, deleteConversation, setCurrentConversation])

  const confirmLeaveGroup = useCallback(async () => {
    if (currentConversation) {
      await leaveGroup(currentConversation)
    }
    setShowLeaveGroupConfirm(false)
    setShowMobileChat(false)
  }, [currentConversation, leaveGroup])

  const totalUnread = conversations.reduce((sum, c) => {
    const count = typeof c.unread_count === 'number' ? c.unread_count : 0
    return sum + count
  }, 0)

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
              <i className="ri-message-3-fill text-white text-2xl"></i>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
            <p className="text-sm text-gray-500">Loading messages...</p>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50 flex flex-col">
      <Header />
      
      {/* Main Content */}
      <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100" style={{ height: 'calc(100vh - 220px)', minHeight: '600px' }}>
          <div className="flex h-full">
            {/* Left Panel - Conversation List */}
            <div className={`w-full lg:w-[380px] flex-shrink-0 flex flex-col bg-white border-r border-gray-100 ${showMobileChat ? 'hidden lg:flex' : 'flex'}`}>
              {/* Header */}
              <div className="h-[72px] bg-white flex items-center justify-between px-5 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-bold text-gray-900">Messages</h1>
                  {totalUnread > 0 && (
                    <span className="px-2.5 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-semibold rounded-full">
                      {totalUnread}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setShowCreateGroup(true)}
                    className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                    title="New group"
                  >
                    <i className="ri-group-line text-gray-600 text-xl"></i>
                  </button>
                  <button
                    onClick={() => setShowNewMessage(true)}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-md transition-all cursor-pointer"
                    title="New chat"
                  >
                    <i className="ri-edit-line text-lg"></i>
                  </button>
                </div>
              </div>

              {/* Search */}
              <div className="px-4 py-3 bg-gray-50/50">
                <div className="relative flex items-center">
                  <div className="absolute left-3 w-5 h-5 flex items-center justify-center">
                    <i className="ri-search-line text-gray-400"></i>
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search conversations..."
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-all"
                  />
                </div>
              </div>

              {/* Filter Tabs */}
              <div className="px-4 py-2 flex items-center gap-2 border-b border-gray-100">
                <button
                  onClick={() => setActiveFilter('all')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeFilter === 'all'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setActiveFilter('unread')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeFilter === 'unread'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Unread
                </button>
                <button
                  onClick={() => setActiveFilter('groups')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeFilter === 'groups'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Groups
                </button>
              </div>

              {/* Conversation List */}
              <ConversationList
                conversations={filteredConversations}
                currentConversation={currentConversation}
                onSelectConversation={handleSelectConversation}
                onNewConversation={() => setShowNewMessage(true)}
                onNewGroup={() => setShowCreateGroup(true)}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
              />
            </div>

            {/* Right Panel - Chat Area */}
            <div className={`flex-1 min-w-0 ${!showMobileChat ? 'hidden lg:flex' : 'flex'}`}>
              <ChatArea
                conversation={activeConversation}
                messages={messages}
                messagesLoading={messagesLoading}
                onSendMessage={handleSendMessage}
                onBack={handleBack}
                onDeleteConversation={handleDeleteConversation}
                onDeleteMessage={handleDeleteMessage}
                onLeaveGroup={handleLeaveGroup}
                onRemoveMember={handleRemoveMember}
                onUpdateGroupSettings={handleUpdateGroupSettings}
                onInviteMembers={handleInviteMembers}
              />
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* New Message Modal */}
      <NewMessageModal
        isOpen={showNewMessage}
        onClose={() => setShowNewMessage(false)}
        onStartConversation={handleStartConversation}
      />

      {/* Create Group Modal */}
      <CreateGroupModal
        isOpen={showCreateGroup}
        onClose={() => setShowCreateGroup(false)}
        onCreateGroup={handleCreateGroup}
      />

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">Delete chat?</h3>
            <p className="text-sm text-gray-500 text-center mb-6">Messages will be removed from this device.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-5 py-2.5 text-sm font-medium text-white bg-red-500 rounded-xl hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leave Group Confirmation */}
      {showLeaveGroupConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowLeaveGroupConfirm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-logout-box-line text-amber-600 text-xl"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">Exit group?</h3>
            <p className="text-sm text-gray-500 text-center mb-6">Only group admins will be notified that you left the group.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLeaveGroupConfirm(false)}
                className="flex-1 px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={confirmLeaveGroup}
                className="flex-1 px-5 py-2.5 text-sm font-medium text-white bg-red-500 rounded-xl hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Exit group
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MessagesPage
